import { Injectable, ComponentFactoryResolver, ComponentRef, Injector } from '@angular/core';
import * as L from 'leaflet';

/**
 * Service dedicated to live relay vehicle rendering and state handling.
 *
 * The concrete types used below assume the same project models/helpers already available
 * in your current implementation.
 */
@Injectable({
    providedIn: 'root',
})
export class LeafletRelayService {
    /** Dynamic tooltip components keyed by vehicle id. */
    readonly tooltipComponents = new Map<string, ComponentRef<any>>();

    /** Live circle markers keyed by vehicle id. */
    readonly liveLayers = new Map<string, L.Marker>();

    /** Trace visibility group keyed by vehicle id. */
    readonly traceGroups = new Map<string, L.LayerGroup>();

    /** Vehicle ids currently active in the map. */
    readonly activeIds = new Set<string>();

    /** Global trace visibility flag. */
    traceVisibility = true;

    /**
     * Creates or updates a relay vehicle with its tooltip, heading marker and traces.
     *
     * @param params Rendering context.
     */
    upsertVehicle(params: {
        map: L.Map;
        messages: any[];
        tooltipFactory: any;
        injector: Injector;
        createColoredDroneIcon: (
            size?: number,
            borderColor?: string,
            borderWidth?: number,
            iconUrl?: string
        ) => L.DivIcon;
    }): void {
        const { map, messages, tooltipFactory, injector, createColoredDroneIcon } = params;
        if (!map || !messages?.length) {
        return;
        }

        const id = messages[0].vid;
        const liveLayer = this.liveLayers.get(id);
        const traceGroup = this.traceGroups.get(id);

        if (!liveLayer || !traceGroup) {
            this.createVehicle({
                map,
                message: messages[0],
                id,
                tooltipFactory,
                injector,
                createColoredDroneIcon,
            });
            return;
        }

        const traceLayers = traceGroup.getLayers();

        messages.forEach((message, index) => {
        if (index === messages.length - 1) {
            this.updateLiveMarker(liveLayer, message);
        } else {
            this.upsertTraceMarker(traceLayers[index] as L.CircleMarker, traceGroup, message);
        }
        });
    }

    /**
     * Removes all relay layers associated with the given vehicle id.
     *
     * @param map Leaflet map.
     * @param id Vehicle id.
     */
    clearVehicle(map: L.Map, id: string): void {
        const liveLayer = this.liveLayers.get(id);
        const traceGroup = this.traceGroups.get(id);
        const tooltip = this.tooltipComponents.get(id);

        if (liveLayer) {
        liveLayer.removeFrom(map);
        this.liveLayers.delete(id);
        }

        if (traceGroup) {
        traceGroup.removeFrom(map);
        this.traceGroups.delete(id);
        }

        if (tooltip) {
        tooltip.destroy();
        this.tooltipComponents.delete(id);
        }

        this.activeIds.delete(id);
    }

    /**
     * Clears all relay resources.
     *
     * @param map Leaflet map.
     */
    clearAll(map: L.Map): void {
        Array.from(this.liveLayers.keys()).forEach((id) => this.clearVehicle(map, id));
    }

    /**
     * Creates a brand-new live relay vehicle.
     *
     * @param params Context required to create markers and tooltip component.
     */
    private createVehicle(params: {
        map: L.Map;
        message: any;
        id: string;
        tooltipFactory: any;
        injector: Injector;
        createColoredDroneIcon: (
            size?: number,
            borderColor?: string,
            borderWidth?: number,
            iconUrl?: string
        ) => L.DivIcon;
    }): void {
        const { map, message, id, tooltipFactory, injector, createColoredDroneIcon } = params;
        const currentLocation = new L.LatLng(message.lat, message.lon);

        const component = tooltipFactory.create(injector);
        component.instance.update = message;
        component.changeDetectorRef.detectChanges();
        this.tooltipComponents.set(id, component);

        const liveMarker = new L.Marker(currentLocation, {
        icon: createColoredDroneIcon(40, '#ffffff'),
        rotationAngle: message.yaw,
        zIndexOffset: 0,
        });
        liveMarker.setRotationOrigin('center center');

        const traceGroup = new L.LayerGroup();
    }
    /**
     * Updates the live marker position and tooltip contents.
     *
     * @param layer Marker to update.
     * @param message Incoming relay message.
     */
    private updateLiveMarker(layer: L.Marker, message: any): void {
        const currentLocation = new L.LatLng(message.lat, message.lon);

        if (currentLocation.lat === 0 && currentLocation.lng === 0) {
        layer.setOpacity(0);
        layer.closeTooltip();
        return;
        }

        layer.openTooltip();
        layer.setOpacity(1);
        layer.setLatLng(currentLocation);
        layer.setRotationAngle(message.yaw);
        layer.setRotationOrigin('center center');

        const tooltip = this.tooltipComponents.get(message.vid);
        if (tooltip) {
        tooltip.instance.update = message;
        tooltip.changeDetectorRef.detectChanges();
        }
    }

    /**
     * Creates or moves a trace marker.
     *
     * @param layer Existing trace marker if any.
     * @param group Trace group to append to when creating a new marker.
     * @param message Incoming relay message.
     */
    private upsertTraceMarker(layer: L.CircleMarker | undefined, group: L.LayerGroup, message: any): void {
        const currentLocation = new L.LatLng(message.lat, message.lon);
        if (currentLocation.lat === 0 && currentLocation.lng === 0) {
        return;
        }

        if (layer) {
        layer.setLatLng(currentLocation);
        return;
        }

        const traceMarker = new L.CircleMarker(currentLocation, {
        radius: 4,
        opacity: 0.7,
        color: 'black',
        });

        group.addLayer(traceMarker);
    }

    isVehicleVisible(id: string): boolean {
        let visible = false;
        this.activeIds.forEach((vehicleId) => {
            if (vehicleId.includes(id)) {
            visible = true;
            }
        });
        return visible;
    }
}